import React, { useState, useEffect } from "react";
import { AppBar, Toolbar, Grid, Typography, TextField, Button, Paper } from "@mui/material";
import { addAttendanceActivityMark, addRemoteAttendance, getLastAttendance, getLastAttendanceActivity } from "../../../api/s10OrganizationApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import MySnackbar from "../../../components/MySnackbar";

interface Attendance {
  EmployeeId: number;
  FullName: string;
  DocumentNumber: string;
  Date: string;
  Time: string;
  MarkType: string;
  HasEntranceOnThisDay: boolean;
}

interface Activity {
  Id: number;
  EmployeeId: number;
  DateTimeActivity: string;
  DailyActivities: string;
}

interface ApiResponse<T> {
  Type: number;
  Message: string;
  Value: T | null;
  Validates: any;
}

const AttendancePage: React.FC = () => {

  const {companyselected, connectionId} = useSelector((state: RootState) => state.auth);

  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [lastAttendance, setLastAttendance] = useState<Attendance | null>(null);
  const [dailyActivity, setDailyActivity] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<number | null | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const DEFAULT_ERROR_MESSAGE = "No estás autorizado para realizar marcaciones remotas.";

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchLastAttendance = async () => {
    const response: {data: ApiResponse<Attendance>} = await getLastAttendance();
    console.log(response)
    if (response.data.Type === 0 && response.data.Value) {
      setLastAttendance(response.data.Value);
      setEmployeeId(response.data.Value.EmployeeId);
    } else {
      setErrorMsg(response.data.Message || DEFAULT_ERROR_MESSAGE);
      setEmployeeId(null);
    }
  };

  const fetchDailyActivity = async () => {
    const response: {data: ApiResponse<Activity>} = await getLastAttendanceActivity();
    if (response.data.Type === 0 && response.data.Value) {
      setDailyActivity(response.data.Value.DailyActivities || "");
    } else {
      setErrorMsg(response.data.Message || "Error obteniendo actividades diarias.");
    }
  };

  const handleActivitySubmit = async () => {
    if (!employeeId) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      return;
    }
    if (!dailyActivity) {
      setErrorMsg("El campo de actividades diarias debe ser completado.");
      return;
    }
    const response = await addAttendanceActivityMark(employeeId, dailyActivity);
    if (response.data.Type === 0) {
      setDailyActivity("");
      setSuccessMsg("Actividad registrada con éxito.");
    } else {
      setErrorMsg(response.data.Message || "Ocurrió un error registrando la actividad.");
    }
  };

  const handleAttendanceMark = async (markType: 0 | 1) => {
    if (!employeeId) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      return;
    }
    setLoading(true);
    const response = await addRemoteAttendance(employeeId, markType);
    setLoading(false);
    if (response.data.Type === 0) {
      setSuccessMsg(`Marcación de ${markType === 0 ? "entrada" : "salida"} registrada con éxito.`);
    } else {
      setErrorMsg(response.data.Message || "Error en la marcación.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!companyselected || !connectionId || employeeId) {
      return;
    }
    fetchLastAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyselected, connectionId]);

  useEffect(() => {
    if (!companyselected || !connectionId || !employeeId) {
      return;
    }
    fetchDailyActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyselected, connectionId, employeeId]);

  return (
    <Grid container>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" color="white">
            Fecha y Hora Actual: {currentTime.toLocaleDateString()} {currentTime.toLocaleTimeString()}
          </Typography>
        </Toolbar>
      </AppBar>

      <Grid container spacing={3} paddingTop={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 2, height: "100%" }}>
            <Typography variant="h6">Última Marcación</Typography>
            {lastAttendance 
              ? (
                <>
                  <Typography>Fecha: {lastAttendance.Date}</Typography>
                  <Typography>Hora: {lastAttendance.Time}</Typography>
                  <Typography>Tipo: {lastAttendance.MarkType}</Typography>
                </>
              ) 
                : employeeId  === null 
                ? (
                  <Typography>Cargando...</Typography>
                )
                : <></>
            }
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Typography variant="h6">Actividades Diarias</Typography>
            <TextField
              fullWidth
              multiline
              disabled={!employeeId}
              rows={4}
              value={dailyActivity}
              onChange={(e) => setDailyActivity(e.target.value)}
              placeholder="Describe tus actividades diarias..."
            />
            <Button
              disabled={!employeeId}
              variant="contained"
              color="primary"
              onClick={handleActivitySubmit}
              sx={{ marginTop: 2 }}
            >
              Guardar Actividades
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 2, textAlign: "center" }}>
            <Button
              variant="contained"
              color="success"
              disabled={loading || !employeeId}
              onClick={() => handleAttendanceMark(0)}
              sx={{ marginRight: 2 }}
            >
              Marcar Entrada
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={loading || !employeeId}
              onClick={() => handleAttendanceMark(1)}
            >
              Marcar Salida
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <MySnackbar
        severity="error"
        msg={errorMsg}
        onClose={() => setErrorMsg("")}
        open={!!errorMsg}
      />

      <MySnackbar
        severity="success"
        msg={successMsg}
        onClose={() => {
          setSuccessMsg("")}
        }
        open={!!successMsg}
      />
    </Grid>
  );
};

export default AttendancePage;
